import { ColumnFormat } from 'common/types/viewColumn';
import { momentDateFormatToDatePickerDateFormat } from 'common/dates';
import moment from 'moment';
import React, { ReactElement } from 'react';
import type { FunctionComponent } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EditCell from './EditCell';
import TypedCell from './TypedCell';
import { TIME_FORMATS } from 'common/DataTypeFormatter';
import { SoQLType } from 'common/types/soql';

type DateColumnFormat = ColumnFormat & { view?: string };

interface Props {
  isDropping: boolean;
  isError: boolean;
  isEditing: boolean;
  updateCell: () => void;
  value: string;
  format: DateColumnFormat;
  type: SoQLType;
}

const DateCell: FunctionComponent<Props> = ({ isEditing, value, updateCell, format, isDropping, type }) => {
  const momentDateFormat =
    (format.view ? TIME_FORMATS[format.view as keyof typeof TIME_FORMATS] : false) ||
    TIME_FORMATS.default_date_time;
  const datePickerDateFormat = momentDateFormatToDatePickerDateFormat(momentDateFormat);

  const wantsUTC = format.view === 'iso_8601_date_time_utc' || format.view === 'iso_8601_date_utc';

  const getDateOrNull = (checkIfDate: string) => {
    const maybeDate = moment(checkIfDate, moment.ISO_8601); // we need to pass in the format, or else invalid strings get huge deprecation warnings
    return maybeDate.isValid() ? maybeDate.toDate() : null;
  };

  if (isEditing) {
    const renderer = (name: string, initialValue: string, change: (v: string) => void): ReactElement => (
      <DatePicker
        key="date-editor"
        name="date-editor"
        showTimeSelect
        timeIntervals={15}
        dateFormat={datePickerDateFormat}
        timeCaption="time"
        popperPlacement="bottom-start"
        selected={getDateOrNull(initialValue)}
        onChange={(newValue: Date | null): void => {
          if (newValue) {
            const newDateFormat = `YYYY-MM-DDTHH:mm:ss.SSS${
              type === SoQLType.SoQLFixedTimestampT ? 'ZZ' : ''
            }`;
            return change(`${moment(newValue).format(newDateFormat)}`);
          }
        }}
      />
    );

    return <EditCell value={value} updateCell={updateCell} renderInput={renderer} />;
  } else {
    let text = value;
    if (value) {
      let m = moment(value);
      m = wantsUTC ? m.utc() : m;
      text = m.format(momentDateFormat);
    }
    return <TypedCell isDropping={isDropping} value={text} format={format} />;
  }
};

export default DateCell;
